import { Track } from "@/src/lib/tracking";
import { useSnapshot } from "valtio/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo } from "react";
import { useParams } from "next/navigation";
import { QUERY_KEYS } from "@config/constant";
import { rootStore } from "@store/rootStore";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import {
  type Member,
  type MemberProduct,
  MemberStatusEnum,
} from "../features/api/memberApi";
import { I18nUtil } from "@utils/i18n";
import {
  ProductType,
  MemberProductLevelEnum,
} from "@components/Member/CurrentPlan/constants";
import { getBridge } from "@/src/lib/bridge/bridge";
import { setShellNativeMenuMemberSnapshot } from "@/src/lib/bridge/shellUserProfileNativeSync";

export enum ProductName {
  Pro = "Pro",
  Expert = "Expert",
}

/** getMember 响应 + useMemberUser 派生的展示/判断字段 */
export type MemberPlanInfo = Member & {
  name: string | undefined;
  isFree: boolean;
  isExpires: boolean;
  isPurchasedMembership: boolean;
  isPro: boolean;
  isExpert: boolean;
  isTrial: boolean;
  renewal_id: string | undefined;
  isMember: boolean;
  isMonthMember: boolean;
  isYearMember: boolean;
  isWeeklyMember: boolean;
  isLifetimeMember: boolean;
  isShowUpdateSearchCount: boolean;
  payCycle: MemberProduct["pay_cycle"];
  level: MemberProductLevelEnum | undefined;
};

export const useMemberUser = () => {
  const { memberApi } = useContext(OspApiClientContext);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const queryClient = useQueryClient();
  const { locale } = useParams<{ locale: string }>();
  const queryKey = [QUERY_KEYS.MEMBER_USE_PLAN, ownerInfo?.id, locale];
  const { data: memberUserData, ...rest } = useQuery<Member | undefined>({
    queryKey,
    enabled: !!memberApi && !!ownerInfo?.id,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    queryFn: async () => {
      try {
        const res = await memberApi?.member?.getMember({
          language: I18nUtil.getBackgroundLanguageKey(locale),
          "enrich.with_credit": true,
          "enrich.with_available_products": true,
        });
        Track.log("info", "Member", "[trace]getMember", { cause: String(res) });
        return res?.data?.data as Member;
      } catch (error) {
        Track.error(error, { tag: "Member", msg: "[trace]getMember error" });
        return undefined;
      }
    },
  });
  const formatData = useMemo((): MemberPlanInfo | undefined => {
    if (!memberUserData) {
      return undefined;
    }
    const currentName = memberUserData?.product?.name;
    const currentLevel = memberUserData?.product?.level;
    const currentType = memberUserData?.product?.type;
    const currentPayCycle =
      memberUserData?.product?.level === MemberProductLevelEnum.FREE
        ? undefined
        : memberUserData?.product?.pay_cycle;
    const memberStatus = memberUserData?.status;
    const isFree = currentLevel === MemberProductLevelEnum.FREE;
    // 当前周期权益的结束时间
    const currentCreditExpireTime = memberUserData?.credit?.end_time ?? 0;
    // 会员计划的结束时间
    const currentExpireTime = memberUserData?.expire_time ?? 0;
    return {
      ...memberUserData,
      name: currentName,
      isFree,
      isExpires: memberStatus === MemberStatusEnum.EXPIRED,
      // isPurchasedMembership: memberStatus === 'EXPIRED' || memberStatus === 'ACTIVE',
      isPurchasedMembership: !!memberStatus, // 如果有值，则肯定是买过会员，要么是EXPIRED或者ACTIVE，如果为空就肯定没有买过
      isPro: currentLevel === MemberProductLevelEnum.PRO,
      isExpert: currentLevel === MemberProductLevelEnum.EXPERT,
      isTrial: currentType === ProductType.TRIAL,
      renewal_id: memberUserData?.product?.renewal_id,
      isMember: !isFree && memberStatus === MemberStatusEnum.ACTIVE,
      isMonthMember:
        !isFree &&
        currentPayCycle === "MONTH" &&
        memberStatus === MemberStatusEnum.ACTIVE,
      isYearMember:
        !isFree &&
        currentPayCycle === "YEAR" &&
        memberStatus === MemberStatusEnum.ACTIVE,
      isWeeklyMember:
        !isFree &&
        currentPayCycle === "WEEK" &&
        memberStatus === MemberStatusEnum.ACTIVE,
      isLifetimeMember:
        !isFree &&
        currentPayCycle === "LIFETIME" &&
        memberStatus === MemberStatusEnum.ACTIVE,
      // 如果用户active，且是自动续费，且with_order的结束时间小于会员的expiretime
      isShowUpdateSearchCount:
        memberStatus === MemberStatusEnum.ACTIVE &&
        !isFree &&
        currentCreditExpireTime < currentExpireTime,
      payCycle: currentPayCycle,
      credit: memberUserData?.credit,
      level: currentLevel,
    };
  }, [memberUserData]);

  const isCached = !!queryClient.getQueryData(queryKey);

  useEffect(() => {
    if (typeof window === "undefined" || !getBridge().isInApp()) {
      return;
    }
    if (!ownerInfo?.id) {
      setShellNativeMenuMemberSnapshot(null);
      return;
    }
    if (memberUserData == null) {
      return;
    }
    if (
      typeof memberUserData === "object" &&
      Object.keys(memberUserData).length === 0
    ) {
      return;
    }
    setShellNativeMenuMemberSnapshot({
      productName: formatData?.name,
      isFree: formatData?.isFree,
      creditsAvailable: formatData?.credit?.available ?? 0,
      status:
        memberUserData.status != null
          ? String(memberUserData.status)
          : undefined,
    });
  }, [
    ownerInfo?.id,
    memberUserData,
    formatData?.name,
    formatData?.isFree,
    formatData?.credit,
    formatData?.status,
  ]);

  return {
    data: formatData,
    isCached,
    ...rest,
  };
};
